import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button, Icon, LazyLoadImage, Modal, Select, Typography } from 'ck-ui';
import { getImgixUrl } from 'ck-utils';
import { isEmailValid } from 'ck-utils/validate';
import layout from 'ck-utils/layout.module.scss';
import { useUpdateEffect } from 'react-use';
import { TermsAndPrivacyLinks } from '../TermsAndPrivacyLinks';
import styles from './BackInStock.module.scss';

const BackInStock = ({ title, product, isHypedFootwear }) => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSignedUp, setHasSignedUp] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('');
  const [selectedVariantError, setSelectedVariantError] = useState(null);

  const validateSelectedVariant = () => {
    setSelectedVariantError(selectedVariant ? null : 'Please select size first.');
    return Boolean(selectedVariant);
  };

  useEffect(() => {
    const handleShowModal = () => {
      setShow(true);
    };

    window.addEventListener('showBackInStock', handleShowModal);
    return () => {
      window.removeEventListener('showBackInStock', handleShowModal);
    };
  }, []);

  useUpdateEffect(() => {
    validateSelectedVariant();
  }, [selectedVariant]);

  if (!product) return null;

  const onClose = () => {
    setShow(false);
  };

  const validateEmail = () => {
    const isValid = isEmailValid(email);
    setEmailError(isValid ? null : 'Please enter a valid email address.');
    return isValid;
  };

  return (
    <Modal show={show} onClose={onClose} isTabletAboveFixedSize>
      <div className={styles.root}>
        <div className="is-flex">
          <LazyLoadImage
            src={`${getImgixUrl(product.featured_image, [
              'fit=crop',
              'h=60',
              'w=60'
            ])}`}
            className={styles.circle}
            alt={title}
          />
          <Typography variant="heading7" className={styles.lh}>{title}</Typography>
        </div>
        <div className={styles.divider} />
        <div className="mt-4">
          {hasSignedUp ? (
            <>
              <Typography variant="heading5" align="centered" uppercase>Thank you!</Typography>
              <Typography
                color="black-bis"
                display="block"
                align="centered"
                className={styles.largeMarginTop}
              >
                We will notify you as soon as this becomes available again.
              </Typography>
              <Typography
                className={classnames(
                  'is-flex',
                  'is-flex-centered',
                  styles.largeMarginTop,
                  styles.tick
                )}
              >
                <Icon color="secondary" name="check" />
              </Typography>
            </>
          ) : (
            <>
              <Typography renderAs="p" color="black-bis">
                {isHypedFootwear
                  ? 'Next time any footwear heat like this drops - I want in!'
                  : 'Don\'t miss the limited units restock incoming - Sign up for VIP notification.'
                }
              </Typography>
              <Select
                size="medium"
                fullwidth
                className={classnames('modal-size-selector mt-1', { error: selectedVariantError })}
                value={selectedVariant}
                onChange={(e) => setSelectedVariant(e.target.value)}
              >
                <option key="default" value="">Select size</option>
                {product.variants.map((variant) => (
                  <option
                    key={variant.id}
                    value={`${variant.id}-${variant.title}-${variant.sku}`}
                  >
                    {variant.title}
                  </option>
                ))}
              </Select>
              {selectedVariantError && (
                <Typography className="mt-1" variant="body-small" display="block" color="danger">{selectedVariantError}</Typography>
              )}
              <input
                id="email"
                value={email}
                type="email"
                placeholder="Email Address"
                className={classnames(styles.input, 'is-inline-block', 'is-full-width', 'body-small mt-1', { [styles.error]: emailError })}
                onChange={e => setEmail(e.target.value)}
                onBlur={validateEmail}
              />
              {emailError && (
                <Typography className="mt-1" variant="body-small" display="block" color="danger">{emailError}</Typography>
              )}
              <TermsAndPrivacyLinks noSMS />
            </>
          )}
          <div className={classnames('is-flex mt-3', layout.justifyCenter)}>
            {hasSignedUp ? (
              <Button onClick={onClose}>
                Close
              </Button>
            ) : (
              <Button
                loading={loading}
                onClick={() => {
                  if (!validateEmail() || !validateSelectedVariant() || loading) return;
                  setLoading(true);
                  const [variantId] = selectedVariant.split('-');

                  const payload = {
                    data: {
                      type: 'back-in-stock-subscription',
                      attributes: {
                        profile: {
                          data: {
                            type: 'profile',
                            attributes: {
                              email,
                            }
                          }
                        },
                        channels: ['EMAIL'],
                      },
                      relationships: {
                        variant: {
                          data: {
                            type: 'catalog-variant',
                            id: `$shopify:::$default:::${variantId}`
                          }
                        }
                      }
                    }
                  };

                  const requestOptions = {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      revision: '2024-05-15'
                    },
                    body: JSON.stringify(payload),
                  };

                  fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${process.env.REACT_APP_KLAVIYO_KEY}`, requestOptions)
                    .then(() => setHasSignedUp(true))
                    .catch(error => {
                      // eslint-disable-next-line
                      console.log('error', error);
                    })
                    .finally(() => setLoading(false));
                }}
              >
                {isHypedFootwear ? 'I WANT VIP ACCESS NEXT TIME' : 'SIGN UP FOR NEXT DROP'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

BackInStock.propTypes = {
  title: PropTypes.string.isRequired,
  product: PropTypes.object.isRequired,
  isHypedFootwear: PropTypes.bool.isRequired,
};

export { BackInStock };
